/**
 * Utility functions to determine the completion status of Pro Calculator sections
 */

/**
 * Determines if the Reference section is complete
 * @param {Object} reference - The reference cookie data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getReferenceStatus = (reference) => {
  if (!reference) {
    return 'neutral'; // Indicate loading or error state?
  }

  const isValidField = (field) => {
    const isValid = field !== null && field !== undefined && String(field).trim() !== '';
    return isValid;
  };

  const { width, length, weightNoIcing, weight } = reference;

  const isWidthValid = isValidField(width);
  const isLengthValid = isValidField(length);
  const isWeightNoIcingValid = isValidField(weightNoIcing);
  const isWeightValid = isValidField(weight);

  if (isWidthValid && isLengthValid && isWeightNoIcingValid && isWeightValid) {
    return 'completed';
  }
  if (isWidthValid || isLengthValid || isWeightNoIcingValid || isWeightValid) {
    return 'incomplete';
  }

  return 'incomplete'; // Default to incomplete if none are filled
};

/**
 * Determines if the Cookie Names section is complete
 * @param {Array} cookieRows - The cookie rows data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getCookieNamesStatus = (cookieRows) => {
  if (!cookieRows || cookieRows.length === 0) {
    return 'neutral'; // Or 'incomplete' if adding the first row is expected
  }

  const allNamed = cookieRows.every(row => row.cookieName && String(row.cookieName).trim() !== '');
  const namedRowsCount = cookieRows.filter(row => row.cookieName && String(row.cookieName).trim() !== '').length;

  if (allNamed) {
    return 'completed';
  } else {
    return 'incomplete';
  }
};

const isValidPositiveNumber = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

/**
 * Determines if the Dimensions section is complete
 * @param {Array} dimensionsRows - The dimensions data
 * @returns {string} - 'completed' or 'incomplete'
 */
export const getDimensionsStatus = (dimensionsRows) => {
  if (!dimensionsRows || dimensionsRows.length === 0) {
    return 'incomplete'; 
  }

  for (const row of dimensionsRows) {
    const { shapeType, width, length, diameter, area } = row;

    if (!shapeType || shapeType === '') {
      return 'incomplete'; 
    }

    switch (shapeType) {
      case 'square':
        if (!isValidPositiveNumber(width)) return 'incomplete';
        break;
      case 'rectangle':
        if (!isValidPositiveNumber(width) || !isValidPositiveNumber(length)) return 'incomplete';
        break;
      case 'circle':
        if (!isValidPositiveNumber(diameter)) return 'incomplete';
        break;
      case 'triangle': 
        if (!isValidPositiveNumber(width) || !isValidPositiveNumber(length)) return 'incomplete';
        break;
      case 'custom':
        if (!isValidPositiveNumber(area)) { 
           return 'incomplete'; 
        } 
        break;
      default:
        return 'incomplete'; 
    }
  }

  return 'completed';
};

/**
 * Determines if the Sets section is complete
 * @param {Boolean} useSets - Whether sets are being used
 * @param {Array} setRows - The sets data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getSetsStatus = (useSets, setRows) => {
  if (!useSets) {
    return 'completed';
  }

  if (setRows.length === 0) {
    return 'incomplete';
  }
  
  const hasSelectedCookie = setRows.some(row => row.cookieId && row.cookieId !== '');
  
  return hasSelectedCookie ? 'completed' : 'incomplete';
};

/**
 * Determines if the Colors section is complete
 * @param {Object} data - The colors data including colorRows and colorNames
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getColorsStatus = (data) => {
  if (!data) return 'completed'; 

  const { colorRows, colorNames, cookieRows } = data;

  if (!colorRows || colorRows.length === 0 || !colorNames) {
    return 'completed';
  }

  if (colorNames.length >= 3) {
    for (const row of colorRows) {
      const percentages = row.percentages || [];
      if (percentages.length !== colorNames.length) return 'incomplete';

      const sum = percentages.reduce((acc, p) => acc + (Number(p) || 0), 0);
      if (Math.abs(sum - 100) > 0.01) {
        return 'incomplete';
      }
    }
  }

  return 'completed';
};

/**
 * Determines if the Orders section is complete
 * @param {Array} orderRows - The orders data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getOrdersStatus = (orderRows) => {
  if (!orderRows || orderRows.length === 0) return 'neutral';
  
  const allOrdersComplete = orderRows.every(row => row.cookieId && row.count);
  const someOrdersComplete = orderRows.some(row => row.cookieId || row.count);
  
  if (allOrdersComplete) {
    return 'completed';
  } else if (someOrdersComplete) {
    return 'incomplete';
  }
  
  return 'neutral';
};

/**
 * Determines if the Results section is complete
 * @param {Object} statuses - The statuses of other sections
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getResultsStatus = (statuses) => {
  const requiredSections = ['reference', 'cookieNames', 'dimensions'];
  
  const allRequiredComplete = requiredSections.every(section => 
    statuses[section] === 'completed'
  );
  
  const anyRequiredIncomplete = requiredSections.some(section => 
    statuses[section] === 'incomplete'
  );
  
  if (allRequiredComplete) {
    return 'completed';
  } else if (anyRequiredIncomplete) {
    return 'incomplete';
  }
  
  return 'neutral';
};

/**
 * Calculates the completion status for all Pro Calculator sections
 * @param {Object} calculatorState - The current state of the Pro Calculator
 * @returns {Object} - An object with the status of each section
 */
export const calculateSectionCompletionStatus = (calculatorState) => {
  if (!calculatorState || Object.keys(calculatorState).length === 0) {
    return {
      reference: 'neutral',
      cookieNames: 'neutral',
      dimensions: 'neutral',
      sets: 'neutral',
      colors: 'neutral',
      orders: 'neutral',
      results: 'neutral',
      all: 'neutral'
    };
  }
  
  const reference = calculatorState.reference || {};
  const cookieRows = calculatorState.cookieRows || [];
  const dimensionsRows = calculatorState.dimensionsRows || [];
  const setRows = calculatorState.setRows || [];
  const orderRows = calculatorState.orderRows || [];
  const useSets = calculatorState.useSets || false;
  
  const colorData = {
    colorRows: calculatorState.colorRows || [],
    colorNames: calculatorState.colorNames || []
  };
  
  const referenceStatus = getReferenceStatus(reference);
  const cookieNamesStatus = getCookieNamesStatus(cookieRows);
  const setsStatus = useSets ? getSetsStatus(useSets, setRows) : 'completed'; // Fixed: Pass useSets and setRows separately
  const dimensionsStatus = getDimensionsStatus(dimensionsRows);
  const colorsStatus = getColorsStatus(colorData);
  const ordersStatus = 'completed';
  const resultsStatus = 'completed';

  const status = {
    reference: referenceStatus,
    cookieNames: cookieNamesStatus,
    dimensions: dimensionsStatus,
    sets: setsStatus,
    colors: colorsStatus,
    orders: ordersStatus,
    results: resultsStatus
  };

  return status;
};
